// services/api.js
import axios from 'axios';

// Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_TIMEOUT = 300000; // 5 minutes

// Instance axios configurée
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'multipart/form-data',
  },
  withCredentials: false, // Désactiver si vous n'utilisez pas de cookies
});

// Intercepteur de requête
apiClient.interceptors.request.use(
  (config) => {
    // Ajouter un timestamp pour éviter le cache
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }
    
    // Afficher l'indicateur de chargement global
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:request:start'));
    }
    
    return config;
  },
  (error) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:request:error'));
    }
    return Promise.reject(error);
  }
);

// Intercepteur de réponse
apiClient.interceptors.response.use(
  (response) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:request:end'));
    }
    
    // Log de performance
    const processTime = response.headers['x-process-time'];
    if (processTime && parseFloat(processTime) > 2) {
      console.warn(`Requête lente: ${response.config.url} (${processTime}s)`);
    }
    
    return response;
  },
  (error) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:request:end'));
    }
    
    // Gestion d'erreur centralisée
    let errorMessage = 'Une erreur est survenue';
    let errorDetails = '';
    
    if (error.response) {
      // Erreur serveur
      const status = error.response.status;
      
      switch (status) {
        case 400:
          errorMessage = 'Requête incorrecte';
          errorDetails = error.response.data?.error || 'Vérifiez les données envoyées';
          break;
        case 413:
          errorMessage = 'Fichier trop volumineux';
          errorDetails = 'La taille maximale est de 15MB';
          break;
        case 429:
          errorMessage = 'Trop de requêtes';
          errorDetails = 'Veuillez patienter avant de réessayer';
          break;
        case 500:
          errorMessage = 'Erreur serveur';
          errorDetails = 'Notre équipe a été notifiée';
          break;
        case 502:
        case 503:
        case 504:
          errorMessage = 'Service temporairement indisponible';
          errorDetails = 'Veuillez réessayer dans quelques instants';
          break;
        default:
          errorMessage = `Erreur ${status}`;
          errorDetails = error.response.data?.error || '';
      }
    } else if (error.request) {
      // Pas de réponse du serveur
      errorMessage = 'Problème de connexion';
      errorDetails = 'Vérifiez votre connexion internet';
    } else {
      // Erreur de configuration
      errorMessage = 'Erreur de configuration';
      errorDetails = error.message;
    }
    
    // Créer une erreur enrichie
    const enrichedError = new Error(errorMessage);
    enrichedError.details = errorDetails;
    enrichedError.original = error;
    enrichedError.status = error.response?.status;
    
    // Émettre un événement global d'erreur
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:error', {
        detail: enrichedError
      }));
    }
    
    return Promise.reject(enrichedError);
  }
);

// Fonctions API principales
export const apiService = {
  // Vérifier la santé du serveur
  async checkHealth() {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  },
  
  // Résumer un fichier (version standard)
  async summarizeFile(file) {
    try {
      // Validation frontend
      if (!file) {
        throw new Error('Aucun fichier sélectionné');
      }
      
      if (file.size > 15 * 1024 * 1024) {
        throw new Error('Fichier trop volumineux (max 15MB)');
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/api/summarize', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes pour ce endpoint
      });
      
      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      console.error('Summarize error:', error);
      throw error;
    }
  },
  
  // Résumer avec streaming (pour gros fichiers)
  async summarizeFileStream(file, callbacks = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('file', file);
      
      let stageData = {
        extraction: false,
        processing: false,
        ai_processing: false,
        finalizing: false,
        complete: false,
      };
      
      xhr.open('POST', `${API_BASE_URL}/api/summarize/stream`);
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          try {
            const lines = xhr.responseText.trim().split('\n');
            let lastValidData = null;
            
            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data = JSON.parse(line);
                  lastValidData = data;
                  
                  // Appeler les callbacks
                  if (data.stage && callbacks.onStageChange) {
                    callbacks.onStageChange(data.stage, data.message);
                    stageData[data.stage] = true;
                  }
                  
                  if (data.progress && callbacks.onProgress) {
                    callbacks.onProgress(data.progress);
                  }
                  
                  if (data.stage === 'complete') {
                    resolve({
                      success: true,
                      summary: data.summary,
                      stages: stageData,
                      chunks: data.chunks_processed,
                    });
                    return;
                  }
                  
                  if (data.error) {
                    reject(new Error(data.error));
                    return;
                  }
                } catch (e) {
                  console.warn('Failed to parse stream line:', line);
                }
              }
            }
            
            if (lastValidData?.error) {
              reject(new Error(lastValidData.error));
            } else {
              reject(new Error('Réponse inattendue du serveur'));
            }
          } catch (e) {
            reject(new Error('Erreur de traitement de la réponse'));
          }
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Erreur réseau'));
      };
      
      xhr.ontimeout = function() {
        reject(new Error('Délai d\'attente dépassé'));
      };
      
      xhr.timeout = 300000; // 5 minutes
      
      // Suivi de la progression du téléchargement
      xhr.upload.onprogress = function(e) {
        if (e.lengthComputable && callbacks.onUploadProgress) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          callbacks.onUploadProgress(percentComplete);
        }
      };
      
      xhr.send(formData);
    });
  },
  
  // Poser une question sur un résumé
  async askQuestion(question, summary) {
    try {
      if (!question || !summary) {
        throw new Error('Question et résumé requis');
      }
      
      const response = await apiClient.post('/api/ask', {
        question: question.substring(0, 500),
        summary: summary.substring(0, 10000),
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Ask error:', error);
      throw error;
    }
  },
  
  // Obtenir les diagnostics
  async getDiagnostics() {
    try {
      const response = await apiClient.get('/api/diagnostics');
      return response.data;
    } catch (error) {
      console.error('Diagnostics error:', error);
      throw error;
    }
  },
};

// Export pour compatibilité
export const summarizeFile = apiService.summarizeFile;
export const askAI = apiService.askQuestion;
export const summarizeFileStream = apiService.summarizeFileStream;

export default apiService;